import { useState } from "react";
import { Pencil, Settings2 } from "lucide-react";
import { Section, StatusPill, KpiChips, Bullets, kpiLabelMap } from "./Common";
import { hmWIB, fmtDate, daysBetween } from "../lib/fmt";
import Comments from "./Comments";
import AdminReportDialog from "./AdminReportDialog";

const ProgramChip = ({ program }) =>
  program ? <span className="mt-1 inline-block rounded-full bg-brand-tint px-2 py-0.5 text-[11px] font-bold text-brand-deep">{program}</span> : null;

const commentProps = (data, r, reload) => ({
  report: r, comments: (data.comments || {})[r.id] || [], canComment: !!data.me?.privileged, meId: data.me?.id, onChanged: reload,
});

export const canEdit = (data, r) =>
  data.me && r.memberId === data.me.id && daysBetween(r.date, data.today) <= data.settings.editWindowDays;

const EditBtn = ({ r, onEdit }) => (
  <button data-testid={`team-edit-${r.id}`} onClick={() => onEdit(r)} className="btn-ghost min-h-[40px] px-3 text-xs"><Pencil size={13} /> Edit</button>
);

const ManageBtn = ({ r, onManage }) => (
  <button data-testid={`team-manage-${r.id}`} onClick={() => onManage(r)} className="btn-ghost min-h-[40px] px-3 text-xs"><Settings2 size={13} /> Kelola</button>
);

const Actions = ({ r, data, onEdit, onManage }) =>
  canEdit(data, r) ? <EditBtn r={r} onEdit={onEdit} /> : data.privileged ? <ManageBtn r={r} onManage={onManage} /> : null;

function Card({ r, m, labels, data, onEdit, onManage, reload }) {
  return (
    <div data-testid={`team-card-${r.id}`} className="card p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-extrabold">{m?.name || r.memberId}</div>
          <div className="text-xs text-[#7a6667]">{r.role} · <b className="text-brand">{r.code}</b></div>
          <ProgramChip program={r.program} />
        </div>
        <StatusPill status={r.status} />
      </div>
      <div className="mt-3 text-sm"><Bullets text={r.activity} /></div>
      <div className="mt-3"><KpiChips kpi={r.kpi} labels={labels} /></div>
      {r.metrics && <p className="mt-2 text-xs text-[#7a6667]">Catatan: {r.metrics}</p>}
      {r.blocker && <p className="mt-3 rounded-xl bg-brand-soft p-2.5 text-sm"><b className="text-brand">Blocker:</b> {r.blocker}</p>}
      {r.tomorrow && <div className="mt-3 text-sm"><div className="text-xs font-bold text-[#7a6667]">Besok</div><Bullets text={r.tomorrow} /></div>}
      <div className="mt-3 flex items-center justify-between text-xs text-[#7a6667]">
        <span>Dikirim {hmWIB(r.submittedAt)}{r.editedAt && <span className="ml-1 font-bold text-amber-700">· diedit</span>}</span>
        <Actions r={r} data={data} onEdit={onEdit} onManage={onManage} />
      </div>
      <Comments {...commentProps(data, r, reload)} />
    </div>
  );
}

export default function TeamReports({ data, onEdit, reload }) {
  const labels = kpiLabelMap(data.jobFunctions);
  const byId = Object.fromEntries(data.team.map((m) => [m.id, m]));
  const rows = data.reports.filter((r) => r.date === data.date).sort((a, b) => a.submittedAt.localeCompare(b.submittedAt));
  const [managing, setManaging] = useState(null);
  return (
    <Section id="team" eyebrow="Laporan Tim" title={fmtDate(data.date)} right={<span className="text-sm font-semibold text-[#7a6667]">{rows.length} laporan</span>}>
      {rows.length === 0 && <div data-testid="team-empty" className="card p-5 text-sm text-[#7a6667]">Belum ada laporan untuk tanggal ini.</div>}
      <div className="grid gap-3 md:hidden" data-testid="team-cards">
        {rows.map((r) => <Card key={r.id} r={r} m={byId[r.memberId]} labels={labels} data={data} onEdit={onEdit} onManage={setManaging} reload={reload} />)}
      </div>
      {rows.length > 0 && (
        <div className="hidden overflow-hidden rounded-2xl border border-brand-line md:block" data-testid="team-table">
          <table className="w-full text-sm">
            <thead className="bg-brand-deep text-left text-xs text-white">
              <tr>{["PIC", "Aktivitas", "Kode", "Status", "KPI", "Blocker", "Besok", "Dikirim", ""].map((h) => <th key={h} className="px-3 py-2.5 font-bold">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-brand-line align-top">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`team-row-${r.id}`} className="hover:bg-brand-soft/60">
                  <td className="px-3 py-3"><div className="font-bold">{byId[r.memberId]?.name || r.memberId}</div><div className="text-xs text-[#7a6667]">{r.role}</div><ProgramChip program={r.program} /></td>
                  <td className="max-w-[280px] px-3 py-3"><Bullets text={r.activity} />{r.metrics && <p className="mt-1 text-xs text-[#7a6667]">{r.metrics}</p>}<Comments {...commentProps(data, r, reload)} /></td>
                  <td className="whitespace-nowrap px-3 py-3 font-extrabold text-brand">{r.code}</td>
                  <td className="px-3 py-3"><StatusPill status={r.status} /></td>
                  <td className="max-w-[200px] px-3 py-3"><KpiChips kpi={r.kpi} labels={labels} /></td>
                  <td className="max-w-[180px] px-3 py-3">{r.blocker || <span className="text-[#9b8a8b]">–</span>}</td>
                  <td className="max-w-[200px] px-3 py-3"><Bullets text={r.tomorrow} /></td>
                  <td className="whitespace-nowrap px-3 py-3 tabular-nums">{hmWIB(r.submittedAt)}{r.editedAt && <div className="text-xs font-bold text-amber-700">diedit</div>}</td>
                  <td className="px-3 py-3"><Actions r={r} data={data} onEdit={onEdit} onManage={setManaging} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <AdminReportDialog report={managing} data={data} ownerName={managing ? byId[managing.memberId]?.name || managing.memberId : ""}
        onClose={() => setManaging(null)} onDone={reload} />
    </Section>
  );
}
